import _ from 'lodash'
import slugify from 'slugify'
import type {
  CreateQueryBodyProps,
  QueryBodyProps,
  QueryBodyAttribute,
  ShowProductsCountProps,
  ProductDetailsVariantProps,
  ProductListingPriceValues,
  BrendaSearchFilter,
  BrendaSearchProduct,
  BrendaSearchParent,
  ProductSearchItem,
  FilterLabel,
} from '~/types'

/*
  Listing query state. One source of truth for the five product listing routes
  (/hire/c/*, /buy/c/*, /hire/p, /buy/p, /p) so the loader's parsing and the
  client's controls can never drift apart.
*/
export const LISTING_PAGE_SIZE = 9

export const LISTING_FILTER_PARAMS = [
  'attribute',
  'price-range',
  'on-sale',
  'order',
] as const

export const LISTING_CANONICAL_PARAMS = [
  'page',
  ...LISTING_FILTER_PARAMS,
] as const

/**
 * Reads listing state off a URL.
 *
 * Page is clamped because createProductQueryBody turns it straight into an
 * offset. Until now only our own controls ever wrote this param, so `?page=abc`
 * (NaN offset) and `?page=-3` (negative offset, Brenda 400) were unreachable.
 * Now the loader reads the URL, crawlers supply whatever they like.
 */
export const parseListingSearchParams = (searchParams: URLSearchParams) => ({
  page: Math.max(1, Math.floor(Number(searchParams.get('page')) || 1)),
  orderBy: searchParams.get('order') ?? undefined,
  onSale: searchParams.get('on-sale') === 'true',
  attributes: searchParams.getAll('attribute'),
  priceRange: searchParams.get('price-range') ?? undefined,
})


/**
 * Pagination information for listing pages
 *
 * @returns {string}
 */
export const shownProductsCount = ({
  limit,
  page = '1',
  total,
}: ShowProductsCountProps): string => {
  const limitNumber = Number(limit)
  const pageNumber = Math.max(1, Number(page) || 1)

  // The first item on this page, one indexed. The previous version returned
  // limit * page - limit, so page one read "Showing 0 - 9", which now ships in
  // the crawlable HTML rather than only appearing after hydration
  const startValue = total > 0 ? limitNumber * (pageNumber - 1) + 1 : 0
  const endValue = Math.min(limitNumber * pageNumber, total)

  return `Showing ${startValue} - ${endValue} out of ${total} entries`
}

// @ref: https://docs.klevu.com/klevucore-reference/enumeration-klevusearchsorting
const getKlevuSortNames = (orderBy: string): string => {
  if (orderBy == 'price-low') {
    return 'PRICE_ASC'
  } else if (orderBy == 'price-high') {
    return 'PRICE_DESC'
  } else {
    return 'RELEVANCE'
  }
}

/**
 * Used for transforming query string attributes into a Brenda/Klevu format to be passed to the API
 *
 * @returns {object}
 */
export const formatAttributeFilters = (
  attributes: string[]
): QueryBodyAttribute[] => {
  // Splits attribute filters by their name and value and formats each as an array with 1 value
  const objectsArr = attributes
    .map(e => _.zipObject(['key', 'values'], decodeURI(e).split('|')))
    .map(e => {
      return {
        key: e.key,
        values: [e.values],
        settings: {
          singleSelect: e.key == 'stockCode' ? false : true,
        },
      }
    })

  // Merges the array item values if they have the same name
  const objectsMerged = _.uniqWith(objectsArr, (pre, cur) => {
    if (pre.key === cur.key) {
      pre.values = cur.values = pre.values.concat(cur.values)

      return true
    }

    return false
  })

  return objectsMerged
}

/**
 * Creates the body object to be sent to the Brenda/Klevu API
 *
 * @returns {QueryBodyProps}
 */
export const createProductQueryBody = ({
  limit,
  page,
  onSale,
  searchTerm,
  orderBy,
  categorySlug,
  productType,
  productCodes,
  attributes,
  priceRange,
  returnFilters = true,
  categoryFilters = [],
}: CreateQueryBodyProps): QueryBodyProps => {
  const limitNumber = limit ? Number(limit) : 9
  const pageNumber = page ? Number(page) : 1
  const queryString = searchTerm ? searchTerm : '*'
  const sortOrder = orderBy ? getKlevuSortNames(orderBy) : 'RELEVANCE'

  // Filter by custom attributes
  const attributeFilters =
    attributes && attributes.length > 0
      ? formatAttributeFilters(attributes)
      : []

  // Fixed price filter
  const priceFilter = priceRange
    ? [
        {
          key: 'klevu_price',
          values: [priceRange],
          settings: {
            singleSelect: true,
          },
        },
      ]
    : []

  // Fixed vertical filter (none customer controlled)
  const typeFilter = productType
    ? [
        {
          key: 'hyraxProductType',
          values: [productType],
          settings: {
            singleSelect: true,
          },
        },
      ]
    : []

  // Fixed stock code filter (none customer controlled)
  // @NOTE: This changes the "per page" value from 9 to 99..
  // That means it can't be used currently on normal paginated pages
  const codesFilter =
    productCodes && productCodes?.length > 0
      ? [
          {
            key: 'stockCode',
            values: productCodes,
            settings: {
              singleSelect: false,
            },
          },
        ]
      : []

  const saleFilter = onSale
    ? [
        {
          key: 'onSale',
          values: [onSale],
          settings: {
            singleSelect: true,
          },
        },
      ]
    : []

  const defaultFilters = [
    'brand',
    'height',
    'width',
    'length',
    'depth',
    'weight',
  ]

  const returnFilterSettings = returnFilters
    ? {
        enabled: true,
        include: [...defaultFilters, ...categoryFilters],
        rangeFilterSettings: [
          {
            key: 'klevu_price',
            minMax: true,
            rangeInterval: 0,
          },
        ],
      }
    : {
        enabled: false,
      }

  const queryBody = {
    query: queryString,
    category: categorySlug ?? '',
    limit: codesFilter?.length ? 16 : limitNumber,
    offset: pageNumber <= 1 ? 0 : pageNumber * limitNumber - limitNumber,
    sort: sortOrder,
    filtersToReturn: returnFilterSettings,
    applyFilters: {
      filters: [
        ...priceFilter,
        ...typeFilter,
        ...attributeFilters,
        ...codesFilter,
        ...saleFilter,
      ],
    },
  }

  return queryBody
}

/**
 * Overwrite a certain set of data if the child exists, otherwise use parent
 *
 * @returns {T}
 */
export const productDataToUse = <T>(parent: T, child: T): T => {
  return child ? child : parent
}

/**
 * Get the active images for a product single page
 *
 * @returns {array}
 */
export const getVariantImages = (
  variants: ProductDetailsVariantProps[],
  selected: string
): { imageUrl: string }[] => {
  const variant = variants.find(e => e.stockCode == selected)

  if (variant?.images) {
    return variant.images
  } else {
    return []
  }
}

/**
 * Formats the attributes for the URL on listing pages
 *
 * @returns {string}
 */
export const formatQueryFilter = (value: string) => {
  return slugify(_.kebabCase(value))
}

/**
 * Checks to see if the price filters are active, or just matching min/max default
 *
 * @returns {boolean}
 */
export const hasActivePriceFilter = ({
  start,
  end,
  min,
  max,
}: ProductListingPriceValues): boolean => {
  if (min == undefined || max == undefined) {
    return false
  }

  if (start == min && end == max) {
    return false
  }

  return true
}

/**
 * Sets the object for our price filter values when received from query
 *
 * @returns {BrendaSearchFilter}
 */
export const formatActivePriceFilter = ({
  start,
  end,
  min,
  max,
}: BrendaSearchFilter): ProductListingPriceValues => {
  return {
    start: start ? start : min ? min : 0,
    end: end ? end : max ? max : 0,
    min: min ?? undefined,
    max: max ?? undefined,
  }
}

export const hasVariations = (options: { name: string; values: string[] }) => {
  return options && options?.values?.length && options?.name !== 'Default'
    ? true
    : false
}

// Options for order by
export const orderByOptions = [
  {
    value: 'relevance',
    label: 'Relevance',
  },
  {
    value: 'price-high',
    label: 'Price (High to Low)',
  },
  {
    value: 'price-low',
    label: 'Price (Low to High)',
  },
]

// Returns the separated filters data for product search
export const mapSearchFiltersData = (
  e?: BrendaSearchFilter[]
): {
  regularFilters: BrendaSearchFilter[]
  priceFilter: BrendaSearchFilter | null
} => {
  if (!e || e?.length == 0) return { regularFilters: [], priceFilter: null }

  return {
    regularFilters: e.filter(x => x.label !== 'klevu_price'),
    priceFilter: e.find(x => x.label == 'klevu_price') ?? null,
  }
}

// Maps the variant brenda data to our shared object format
export const mapSearchBrendaVariant = (
  e: BrendaSearchProduct
): ProductSearchItem => {
  return {
    name: e.name,
    shortDescription: e.shortDescription,
    productType: e.parentProduct?.hyraxProductType,
    image: e.images[0],
    guidePrice: e.guidePrice,
    salePrice: e?.salePrice,
    url: {
      stockCode: e.parentProduct?.stockCode,
      slug: e.parentProduct?.slug,
      option: e.option,
    },
    options: e.parentProduct?.options,
    bulletPoints: e.bulletPoints,
    promotionName: e.promotionName,
    variantStockCode: e.stockCode,
  }
}

// Maps the parent brenda data to our shared object format
export const mapSearchBrendaParent = (
  e: BrendaSearchParent
): ProductSearchItem => {
  return {
    name: e.name,
    shortDescription: e.shortDescription,
    productType: e.hyraxProductType,
    image: e.image,
    guidePrice: e.guidePrice,
    salePrice: e?.salePrice,
    url: {
      stockCode: e.stockCode,
      slug: e.slug,
    },
    options: e.options,
    bulletPoints: e?.bulletPoints,
    promotionName: e?.promotionName,
  }
}

export const getCustomFilterLabel = (
  name: string,
  value: string,
  filterLabels?: FilterLabel[]
) => {
  const filter = filterLabels?.find(e => e?.name == name)
  const label = filter?.options?.find(e => e?.value == value)

  return label?.label ? label.label : value
}
